import { createRef, useEffect } from 'react';
import Logger from '@medflex/utils/Logger';

/**
 * Handler to disable rubber band scrolling effect on mobile devices (mostly iOS).
 * @returns Object With function "bindToElement" to bind handler to DOM element
 *    and function "unbindFromElement" to unbind handler from bound DOM element.
 */
const disableRubberBandScrollingHandler = () => {
  /** DOM element handler is "bound" to */
  let elem = null;
  /** Touch point's Y coordinate relative to viewport */
  let touchPointY = 0;

  const getElementIdOrDataComponentValue = () =>
    (elem && (elem.id || (elem.dataset && elem.dataset.component))) || '?';

  const disableRubberBand = event => {
    const deltaY = event.targetTouches[0].clientY - touchPointY;

    // swiping down and scrolled to top
    // check if scrollHeight is actually different from clientHeight
    if (deltaY > 0 && elem.scrollTop === 0 && elem.scrollHeight !== elem.clientHeight) {
      event.preventDefault();
    }

    // swiping up and scrolled to bottom
    // check if scrollHeight is actually different from clientHeight
    if (
      deltaY < 0 &&
      elem.scrollHeight !== elem.clientHeight &&
      elem.scrollHeight - elem.scrollTop <= elem.clientHeight
    ) {
      event.preventDefault();
    }
  };

  const touchStartListener = event => {
    if (event.targetTouches.length === 1) {
      touchPointY = event.targetTouches[0].clientY;
    }
  };

  const touchMoveListener = event => {
    if (event.targetTouches.length === 1) {
      disableRubberBand(event);
    }
  };

  const bindToElement = element => {
    if (elem) {
      Logger.warn('[scrollHandler] already bound');
    } else if (!element) {
      Logger.warn('[scrollHandler] no element to bind to');
    } else {
      elem = element;
      // non-passive essential to enable event's prevent default handling in browser
      elem.addEventListener('touchstart', touchStartListener, { passive: false });
      elem.addEventListener('touchmove', touchMoveListener, { passive: false });
      Logger.log(`[scrollHandler] bound to ${getElementIdOrDataComponentValue()}`);
    }
  };

  const unbindFromElement = () => {
    if (elem) {
      elem.removeEventListener('touchstart', touchStartListener, { passive: false });
      elem.removeEventListener('touchmove', touchMoveListener, { passive: false });
      elem = null;
      Logger.log(`[scrollHandler] unbound from ${getElementIdOrDataComponentValue()}`);
    } else {
      Logger.log('[scrollHandler] already unbound');
    }
  };

  return { bindToElement, unbindFromElement };
};

const useDisableRubberBandScrolling = () => {
  const ref = createRef();

  useEffect(() => {
    const handler = disableRubberBandScrollingHandler();
    handler.bindToElement(ref.current);
    return handler.unbindFromElement;
  }, [ref.current]);

  return ref;
};

export default useDisableRubberBandScrolling;
